import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { TransactionService, Transaction, TransactionDetail } from '../services/transaction.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-transaction-details',
  templateUrl: './transaction-details.component.html',
  styleUrls: ['./transaction-details.component.scss']
})
export class TransactionDetailsComponent implements OnInit, OnDestroy {

  @Input() jobId: number;
  @Input() jobRefParent?: number;

  transactions: Transaction[] = [];
  relatedTransactions: Transaction[] = [];
  isLoading = false;
  errorMessage = '';
  private destroy$ = new Subject<void>();

  constructor(private transactionService: TransactionService) { }

  ngOnInit() {
    if (this.jobId) {
      this.loadTransactions();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load transactions for the job
   */
  loadTransactions() {
    this.isLoading = true;
    this.transactionService.getJobTransactions(this.jobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.transactions = response.data || [];
          this.isLoading = false;
        },
        error: (error) => {
          this.errorMessage = 'Failed to load transactions.';
          console.error('Error loading transactions:', error);
          this.isLoading = false;
        }
      });
  }

  /**
   * Load all related transactions (parent and child jobs)
   */
  loadRelatedTransactions() {
    this.isLoading = true;
    this.transactionService.getRelatedJobTransactions(this.jobId, this.jobRefParent)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.relatedTransactions = response.data || [];
          this.isLoading = false;
        },
        error: (error) => {
          this.errorMessage = 'Failed to load related transactions.';
          console.error('Error loading related transactions:', error);
          this.isLoading = false;
        }
      });
  }

  /**
   * Get transaction type display name
   */
  getTypeName(transaction: Transaction): string {
    return this.transactionService.getTransactionTypeDisplayName(transaction.transaction_type);
  }

  /**
   * Get sub type display name
   */
  getSubTypeName(transaction: Transaction): string {
    return this.transactionService.getSubTypeName(transaction.transaction_type, transaction.transaction_sub_type);
  }

  /**
   * Format amount as currency
   */
  formatCurrency(amount: number): string {
    return this.transactionService.formatCurrency(amount);
  }

  /**
   * Get transaction category
   */
  getCategory(transaction: Transaction): string {
    return this.transactionService.getTransactionCategory(transaction);
  }

  /**
   * Check if transaction is a charge
   */
  isCharge(transaction: Transaction): boolean {
    return this.transactionService.isChargeTransaction(transaction);
  }

  /**
   * Check if transaction is a payment
   */
  isPayment(transaction: Transaction): boolean {
    return this.transactionService.isPaymentTransaction(transaction);
  }

  /**
   * Check if transaction is an adjustment
   */
  isAdjustment(transaction: Transaction): boolean {
    return this.transactionService.isAdjustmentTransaction(transaction);
  }

  /**
   * Get CSS class for transaction type
   */
  getTransactionClass(transaction: Transaction): string {
    if (this.isCharge(transaction)) {
      return 'transaction-charge';
    } else if (this.isPayment(transaction)) {
      return 'transaction-payment';
    } else if (this.isAdjustment(transaction)) {
      return 'transaction-adjustment';
    }
    return '';
  }

  /**
   * Calculate total charges
   */
  getTotalCharges(): number {
    return this.transactions
      .filter(t => this.isCharge(t))
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
  }

  /**
   * Calculate total payments
   */
  getTotalPayments(): number {
    return this.transactions
      .filter(t => this.isPayment(t))
      .reduce((sum, t) => sum + t.amount, 0);
  }

  /**
   * Calculate total adjustments
   */
  getTotalAdjustments(): number {
    return this.transactions
      .filter(t => this.isAdjustment(t))
      .reduce((sum, t) => sum + t.amount, 0);
  }

  /**
   * Calculate balance
   */
  getBalance(): number {
    const charges = this.getTotalCharges();
    const payments = this.getTotalPayments();
    const adjustments = this.getTotalAdjustments();
    return charges - payments + adjustments;
  }

  /**
   * Group transactions by category
   */
  getTransactionsByCategory(): { [key: string]: Transaction[] } {
    const grouped: { [key: string]: Transaction[] } = {};

    this.transactions.forEach(transaction => {
      const category = this.getCategory(transaction);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(transaction);
    });

    return grouped;
  }

  /**
   * Get completion charges (user has to pay)
   */
  getCompletionCharges(): Transaction[] {
    return this.transactions.filter(t =>
      t.transaction_type === 'charge' &&
      t.transaction_sub_type === 'Completion' &&
      t.amount > 0
    );
  }

  /**
   * Get total completion charges amount
   */
  getTotalCompletionCharges(): number {
    return this.getCompletionCharges()
      .reduce((sum, t) => sum + t.amount, 0);
  }

  /**
   * Check if there are related jobs (parent or children)
   */
  hasRelatedJobs(): boolean {
    return this.relatedTransactions && this.relatedTransactions.length > 0;
  }
}
